import React, { useState } from "react";
import { Wand2, Drum, Volume2, VolumeX, Eraser, Music2 } from "lucide-react";
import { toast } from "sonner";
import { useStudio } from "@/studio/StudioContext";
import { INSTRUMENT_LIST, DRUM_KIT_LIST } from "@/audio/engine";

// Curated per-role instrument choices (full palette is available; these read cleanest).
const ROLE_META = {
  bass: { label: "Bass", color: "#ff007f" },
  chord: { label: "Chords", color: "#ffb800" },
  lead: { label: "Lead", color: "#00e5ff" },
};

export default function RemixBar() {
  const { engine } = useStudio();
  const [, setLocal] = useState(0);
  const u = () => setLocal((n) => n + 1);

  const roles = engine.started ? engine.roleInstruments : null;
  if (!roles) return null; // only after an AI beat / a loaded song with parts

  const swapKit = async (kitId) => {
    engine.ensureStarted();
    await engine.setDrumKit(kitId);
    const label = (DRUM_KIT_LIST.find((k) => k.id === kitId) || {}).label || kitId;
    toast.success(`Drums → ${label.replace(" ✦", "")}`);
    u();
  };

  const swapRole = (role, instId) => {
    engine.ensureStarted();
    engine.setRoleInstrument(role, instId);
    const label = (INSTRUMENT_LIST.find((i) => i.id === instId) || {}).label || instId;
    toast.success(`${ROLE_META[role].label} → ${label.replace(" ✦", "")}`);
    u();
  };

  const toggleMute = (role) => {
    engine.ensureStarted();
    const muted = engine.toggleRoleMute(role);
    toast(muted ? `${ROLE_META[role].label} muted` : `${ROLE_META[role].label} on`);
    u();
  };

  const drumsOnly = () => {
    engine.ensureStarted();
    const allMuted = ["bass", "chord", "lead"].every((r) => engine.roleMute && engine.roleMute[r]);
    ["bass", "chord", "lead"].forEach((r) => engine.setRoleMute(r, !allMuted));
    toast.success(allMuted ? "All parts back on" : "Drums only — melody muted");
    u();
  };

  const clearMelody = () => {
    engine.ensureStarted();
    engine.clearNotes();
    toast.success("AI melody cleared — drums kept");
    u();
  };

  const hornStabs = () => {
    engine.ensureStarted();
    engine.addHornStabs("brassreal");
    toast.success("Horn stabs added — funk/soul brass hits");
    u();
  };

  const cls =
    "px-2 py-1 rounded-sm text-[10px] hw-label border border-[#2a2a2a] bg-[#0a0a0a] text-neutral-200 focus:border-[#00e5ff] outline-none cursor-pointer max-w-[150px]";

  return (
    <div
      data-testid="remix-bar"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2 border-b border-[#262626] bg-[#0c0c0c]"
    >
      <span className="hw-label flex items-center gap-1.5 text-[#00e5ff]">
        <Wand2 size={13} /> Remix
        <span className="text-neutral-500 normal-case tracking-normal text-[10px]">
          swap sounds live — no regenerate
        </span>
      </span>

      <label className="flex items-center gap-1.5">
        <Drum size={12} className="text-[#ffb800]" />
        <span className="hw-label text-[8px] text-neutral-400">Kit</span>
        <select
          data-testid="remix-kit"
          value={engine.drumKit || "acoustic"}
          onChange={(e) => swapKit(e.target.value)}
          title="Swap the drum kit under this beat"
          className={cls}
          style={{ color: "#ffb800" }}
        >
          {DRUM_KIT_LIST.map((k) => (
            <option key={k.id} value={k.id}>
              {k.label.replace(" ✦", "")}
            </option>
          ))}
        </select>
      </label>

      {Object.keys(ROLE_META).map((role) => {
        const muted = !!(engine.roleMute && engine.roleMute[role]);
        return (
          <label key={role} className="flex items-center gap-1.5">
            <button
              data-testid={`remix-mute-${role}`}
              onClick={() => toggleMute(role)}
              title={muted ? `Unmute ${ROLE_META[role].label.toLowerCase()}` : `Mute ${ROLE_META[role].label.toLowerCase()} (keep just the beat)`}
              className="w-6 h-6 rounded-sm border flex items-center justify-center transition-colors shrink-0"
              style={{
                borderColor: muted ? "#ff3b3b55" : `${ROLE_META[role].color}55`,
                color: muted ? "#ff5555" : ROLE_META[role].color,
                background: muted ? "rgba(255,59,59,0.10)" : "transparent",
              }}
            >
              {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
            </button>
            <span className="hw-label text-[8px] text-neutral-400">{ROLE_META[role].label}</span>
            <select
              data-testid={`remix-${role}`}
              value={roles[role] || ""}
              onChange={(e) => swapRole(role, e.target.value)}
              title={`Swap the ${ROLE_META[role].label.toLowerCase()} instrument`}
              className={cls}
              style={{ color: ROLE_META[role].color, opacity: muted ? 0.4 : 1 }}
            >
              {INSTRUMENT_LIST.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.label.replace(" ✦", "")}
                </option>
              ))}
            </select>
          </label>
        );
      })}

      <button
        data-testid="remix-drums-only"
        onClick={drumsOnly}
        title="Mute all melodic parts and keep just the drum beat (toggle)"
        className="px-2.5 py-1 rounded-sm text-[10px] hw-label border border-[#2a2a2a] text-neutral-300 hover:border-[#ffb800] hover:text-[#ffb800] transition-colors flex items-center gap-1.5"
      >
        <Drum size={11} /> Drums Only
      </button>

      <button
        data-testid="remix-clear-melody"
        onClick={clearMelody}
        title="Permanently remove the AI synth/melody parts (keeps your drums) so you can build from scratch"
        className="px-2.5 py-1 rounded-sm text-[10px] hw-label border border-[#2a2a2a] text-neutral-300 hover:border-[#ff3333] hover:text-[#ff3333] transition-colors flex items-center gap-1.5"
      >
        <Eraser size={11} /> Clear Melody
      </button>

      <button
        data-testid="remix-horn-stabs"
        onClick={hornStabs}
        title="Drop a funk/soul brass-stab riff over this beat (follows the song key)"
        className="px-2.5 py-1 rounded-sm text-[10px] hw-label border border-[#2a2a2a] text-neutral-300 hover:border-[#ffb800] hover:text-[#ffb800] transition-colors flex items-center gap-1.5"
      >
        <Music2 size={11} /> Horn Stabs
      </button>
    </div>
  );
}
